import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Scaling } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const SEEN = "nbc-finals-seen";
const CAL_SEEN = "nbc-cal-seen";
const when = (iso) => new Date(iso).toLocaleDateString("en-GB", { timeZone: "America/New_York", day: "numeric", month: "short" });

export function Notifications() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [games, setGames] = useState([]);
  const [cal, setCal] = useState(null);
  const [seen, setSeen] = useState(() => localStorage.getItem(SEEN) || "");
  const [calSeen, setCalSeen] = useState(() => localStorage.getItem(CAL_SEEN) || "");
  const navigate = useNavigate();
  const load = useCallback(() => {
    api.get("/notifications").then((r) => setGames(r.data)).catch(() => {});
    if (isAdmin) api.get("/points-calibration/weekly").then((r) => setCal(r.data.run)).catch(() => {});
  }, [isAdmin]);
  useEffect(() => { load(); const t = setInterval(load, 120000); return () => clearInterval(t); }, [load]);

  const calUnread = cal && cal.week !== calSeen ? 1 : 0;
  const unread = games.filter((g) => g.date_utc > seen).length + calUnread;
  const [open, setOpen] = useState(false);
  const onOpen = (next) => {
    setOpen(next);
    if (!next) return;
    if (games.length) { localStorage.setItem(SEEN, games[0].date_utc); setSeen(games[0].date_utc); }
    if (cal) { localStorage.setItem(CAL_SEEN, cal.week); setCalSeen(cal.week); }
  };
  return (
    <Popover open={open} onOpenChange={onOpen}>
      <PopoverTrigger className="nbc-bell" aria-label={`Notifications${unread ? `, ${unread} new` : ""}`} data-testid="notifications-bell">
        <Bell size={19} />
        {unread > 0 && <i data-testid="notifications-count">{unread > 9 ? "9+" : unread}</i>}
      </PopoverTrigger>
      <PopoverContent align="end" className="nbc-notif" data-testid="notifications-panel">
        {isAdmin && cal && cal.suggested && Object.keys(cal.suggested).length > 0 && (
          <div data-testid="notification-calibration" style={{ marginBottom: 10, padding: "8px 10px", borderRadius: 8, border: "1px solid #3a6", background: "rgba(95,240,192,0.08)" }}>
            <button type="button" className="nbc-notif-cal-head" onClick={() => { setOpen(false); navigate("/settings"); }}
                    style={{ display: "flex", alignItems: "center", gap: 6, color: "#8ff", fontWeight: 700, background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}>
              <Scaling size={13} />
              {cal.status === "auto_applied" ? `Αυτόματη βαθμονόμηση (${cal.week})` : `Πρόταση βαθμονόμησης (${cal.week})`}
            </button>
            <p style={{ margin: "4px 0 2px", fontSize: 11, opacity: 0.8 }}>
              {cal.status === "auto_applied"
                ? `Εφαρμόστηκαν αυτόματα αλλαγές σε ${Object.keys(cal.suggested).length} ομάδες.`
                : `${Object.keys(cal.suggested).length} ομάδες χρειάζονται ρύθμιση.`}
            </p>
            {(cal.teams || []).map((t) => (
              <div key={t.team_id} data-testid={`notification-cal-team-${t.team_id}`} style={{ fontSize: 11.5, padding: "1px 0" }}>
                <b>{t.code}</b>: −{t.current_pct}% → <b style={{ color: "#5ff0c0" }}>−{t.suggested_pct}%</b>{" "}
                <span style={{ opacity: 0.65 }}>(bias {t.bias_after > 0 ? "+" : ""}{t.bias_after})</span>
              </div>
            ))}
            <button type="button" onClick={() => { setOpen(false); navigate("/settings"); }} data-testid="notification-cal-open"
                    style={{ marginTop: 6, fontSize: 11, color: "#5ff0c0", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
              {cal.status === "auto_applied" ? "Προβολή στα Settings →" : "Έγκριση/Ρύθμιση στα Settings →"}
            </button>
          </div>
        )}
        <h4>Final scores</h4>
        {games.map((g) => {
          const awayWin = g.away_pts > g.home_pts;
          return (
            <button key={g.id} type="button" className="nbc-notif-row" onClick={() => { setOpen(false); navigate(`/?day=${g.game_day}&game=${g.id}`); }} data-testid={`notification-${g.id}`}>
              <small>{when(g.date_utc)}</small>
              <span className={awayWin ? "w" : ""}><img src={g.away.logo} alt="" />{g.away.code} <b>{g.away_pts}</b></span>
              <span className={awayWin ? "" : "w"}><b>{g.home_pts}</b> {g.home.code}<img src={g.home.logo} alt="" /></span>
              <em>FINAL</em>
            </button>
          );
        })}
        {!games.length && <p data-testid="notifications-empty">No finished games in the last 3 days.</p>}
      </PopoverContent>
    </Popover>
  );
}
